import React, { useCallback, useMemo, useState } from 'react';

import { Box, IconButton, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { generatePath, useHistory } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import AssetsListPrintTemplate from 'components/_print/_templates/AssetsListPrintTemplate/AssetsListPrintTemplate';
import PrintComponent from 'components/_print/PrintComponent/PrintComponent';
import fetchPaginatedPrintData from 'components/_print/utils/fetchPaginatedPrintData';
import ColoredButton from 'components/ColoredButton';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import FooterButton from 'components/FooterButton';
import Section from 'components/Section';
import Table from 'components/Table';
import UserAvatar from 'components/UserAvatar/UserAvatar';
import assets, { assetsApi } from 'config/api/assets/assets';
import { parseAllAssetForFE } from 'config/api/assets/assets.parsers';
import { Asset } from 'config/api/assets/assets.types';
import ROLES from 'config/constants/ROLES';
import DIRECTIONS from 'config/constants/sortDirections';
import EVENTS from 'config/events/pubsub';
import safeT from 'helpers/safeT/safeT';
import useApiCall from 'hooks/useApiCall';
import useIsOwner from 'hooks/useIsOwner/useIsOwner';
import usePagination from 'hooks/usePagination';
import usePermissions from 'hooks/usePermissions';
import usePrepareAvailableFilters from 'hooks/usePrepareAvailableFilters/usePrepareAvailableFilters';
import useSubscription from 'hooks/useSubscription';
import assets_messages from 'messages/assets_messages';
import general_messages from 'messages/general_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import PATHS from 'router/PATHS';
import assetsCategoryFilter from 'services/_filters/parsers/_assetsFilters/assetsCategoryFilter';
import assetsImportanceFilter from 'services/_filters/parsers/_assetsFilters/assetsImportanceFilter';
import availabilityFilter from 'services/_filters/parsers/_assetsFilters/availabilityFilter';
import confidentialityFilter from 'services/_filters/parsers/_assetsFilters/confidentialityFilter';
import identifiableInformationFilter from 'services/_filters/parsers/_assetsFilters/identifiableInformationFilter';
import incompleteAssetsFilter from 'services/_filters/parsers/_assetsFilters/incompleteAssetsFilter';
import integrityChangeControlFilter from 'services/_filters/parsers/_assetsFilters/integrityChangeControlFilter';
import legalDemandsFilter from 'services/_filters/parsers/_assetsFilters/legalDemandsFilter';
import myAssetsFilter from 'services/_filters/parsers/_assetsFilters/myAssetsFilter';
import sensitiveIndividualInformationFilter from 'services/_filters/parsers/_assetsFilters/sensitiveIndividualInformationFilter';
import parseFiltersForRequest from 'services/parseFiltersForRequest';
import { Id } from 'types/Id';
import { TableDataMappingRow } from 'types/Table';

const ASSET_DESCRIPTION_PREVIEW_LENGTH = 150;

const getAssetDescriptionPreview = (description?: string) => {
  if (!description) return '';

  const normalizedDescription = description.replace(/\r\n/g, '\n');

  if (normalizedDescription.length <= ASSET_DESCRIPTION_PREVIEW_LENGTH) {
    return normalizedDescription;
  }

  return `${normalizedDescription.slice(0, ASSET_DESCRIPTION_PREVIEW_LENGTH).trimEnd()}...`;
};

type Props = {
  actionAddon?: React.ReactNode;
  sectionTitle?: string;
  sectionTitleVariant?: string;
};

const AssetsTable: React.FC<Props> = ({ actionAddon, sectionTitle, sectionTitleVariant }) => {
  const [isAssetsManager] = usePermissions([ROLES.ORGANIZATION.ASSETS_MANAGER]);
  const { t } = useTranslation();
  const { apiCall } = useApiCall();
  const { enqueueSnackbar } = useSnackbar();
  const [sort, setSort] = useState<{ key: string | null; direction: string | null }>({ key: null, direction: null });
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');
  const onSort = (key: string, direction: string) => setSort({ key, direction });
  const [printAssets, setPrintAssets] = useState<Asset[]>([]);
  const [printCreatedAt, setPrintCreatedAt] = useState(new Date());
  const [printLoading, setPrintLoading] = useState(false);
  // @ts-ignore
  const { showConfirmationModal } = useConfirmationModalContext();
  const { checkIsOwner } = useIsOwner();
  const history = useHistory();

  const availableFilters = usePrepareAvailableFilters({
    category_id: assetsCategoryFilter,
    incomplete_assets: incompleteAssetsFilter,
    only_my_assets: myAssetsFilter,
    confidentiality: confidentialityFilter,
    integrity_change_control: integrityChangeControlFilter,
    availability: availabilityFilter,
    identifiable_information: identifiableInformationFilter,
    sensitive_individual_information: sensitiveIndividualInformationFilter,
    assets_importance: assetsImportanceFilter,
    legal_demands: legalDemandsFilter,
  });
  const filtersForRequest = useMemo(() => parseFiltersForRequest(filters, {}, availableFilters), [availableFilters, filters]);

  const orderBy = useMemo(() => {
    const { key, direction } = sort;
    if (!key || !direction) return null;
    return direction === DIRECTIONS.ASC ? `-${key}` : key;
  }, [sort]);

  const {
    data: tableData,
    getData: getTableData,
    hasNextPage,
    loading,
    refreshData,
  } = usePagination({
    endpointFunc: assets.getAllAssets,
    limits: [30, 20, 10],
    dependencies: [sort, filters, search],
    filters: filtersForRequest,
    search,
    sort,
  });

  const deleteMutation = useMutation(['delete asset'], assetsApi.removeAsset, {
    onSuccess: refreshData,
  });

  const onEdit = async (id: Id) => {
    history.push(generatePath(`${PATHS.ASSETS_EDIT}/:assetId`, { assetId: id }));
  };

  const getRowHref = ({ id, owner }: Asset) => {
    const canView = checkIsOwner(owner.id) || isAssetsManager;
    return canView ? generatePath(`${PATHS.ASSETS}/:assetId`, { assetId: id }) : null;
  };

  const onDelete = async ({ name, id }: Asset) => {
    const shouldProceed = await showConfirmationModal({
      title: t(assets_messages.remove_asset_modal.title),
      body: t(assets_messages.remove_asset_modal.body, { assetName: name }),
    });
    if (shouldProceed) {
      deleteMutation.mutate(id);
    }
  };

  useSubscription(EVENTS.ASSETS_UPDATED, refreshData);

  const renderContextMenu = useCallback(
    (data: Asset) => {
      const rowHref = getRowHref(data);
      const elements = [
        ...(rowHref ? [{ label: t(general_messages.open), onClick: () => history.push(rowHref) }] : []),
        ...(isAssetsManager || checkIsOwner(data.owner.id) ? [{ label: t(general_messages.edit), onClick: () => onEdit(data.id) }] : []),
        ...(isAssetsManager ? [{ label: t(general_messages.delete), onClick: () => onDelete(data) }] : []),
      ];

      if (!elements.length) return null;
      return (
        <ContextMenu elements={elements} id={`assets-table-row-${data.id}`}>
          <IconButton>
            <MoreIcon />
          </IconButton>
        </ContextMenu>
      );
    },
    [checkIsOwner, history, isAssetsManager, t],
  );

  const dataMapping = useMemo(() => {
    const base: TableDataMappingRow<Asset>[] = [
      {
        id: 'name',
        width: '20%',
        label: t(assets_messages.assets_table.name),
        get: ({ name }) => name,
      },
      {
        id: 'owner',
        width: '20%',
        label: t(assets_messages.assets_table.owner),
        get: ({ owner }) => <UserAvatar showName userData={owner.profile} userId={owner.id} />,
      },
      {
        id: 'category',
        width: '20%',
        label: t(assets_messages.assets_table.category),
        get: ({ category }) => safeT(t, category.nameTranslationKey, category.name),
      },
      {
        id: 'description',
        width: '34%',
        label: t(assets_messages.assets_table.description),
        get: ({ info }) => <Typography style={{ whiteSpace: 'pre-line' }}>{getAssetDescriptionPreview(info.assetDescription)}</Typography>,
        blockSorting: true,
      },
    ];

    base.push({
      id: 'context',
      width: '6%',
      label: '',
      isAddon: true,
      get: (data): React.ReactNode => renderContextMenu(data) || ' ',
      blockSorting: true,
    });
    return base;
  }, [t, tableData, isAssetsManager]);

  const preparePrint = useCallback(
    async (onPrint: ReturnType<typeof useReactToPrint>) => {
      if (printLoading) return;
      setPrintLoading(true);
      try {
        const rows = await fetchPaginatedPrintData<Parameters<typeof parseAllAssetForFE>[0][number]>({
          endpointFunc: assets.getAllAssets,
          apiCall,
          requestConfig: { data: { ...filtersForRequest, ...(orderBy && { order_by: [orderBy] }), ...(search && { search }) } },
        });
        setPrintAssets(parseAllAssetForFE(rows));
        setPrintCreatedAt(new Date());
        window.setTimeout(() => onPrint?.(), 0);
      } catch (error) {
        enqueueSnackbar(t(general_messages.api_error), { variant: 'error' });
      } finally {
        setPrintLoading(false);
      }
    },
    [apiCall, enqueueSnackbar, filtersForRequest, orderBy, printLoading, search, t],
  );

  const printAction = (
    <Box alignItems='center' display='flex' flexWrap='wrap' gridGap={8} justifyContent='flex-end'>
      <PrintComponent
        ctaRenderer={({ onPrint }) => (
          // @ts-ignore
          <ColoredButton
            customColor='secondary'
            disabled={printLoading}
            onClick={() => preparePrint(onPrint)}
            showLoader={printLoading}
            variant='outlined'
          >
            {t(assets_messages.buttons.print_assets)}
          </ColoredButton>
        )}
        documentTitle={t(assets_messages.print.assets_title)}
        templateComponent={<AssetsListPrintTemplate assets={printAssets} printedAt={printCreatedAt} />}
      />
      {actionAddon}
    </Box>
  );

  const tableContent = (
    <>
      <Table
        clickableRow
        data={tableData && parseAllAssetForFE(tableData)}
        dataMapping={dataMapping}
        enableSearch
        filtersService={{ availableFilters, filters, setFilters }}
        getRowHref={getRowHref}
        onSearch={setSearch}
        onSort={onSort}
        refetching={loading}
        searchValue={search}
        sort={sort}
      />
      {hasNextPage && (
        // @ts-ignore
        <FooterButton loading={loading} onClick={getTableData}>
          {t(general_messages.show_more)}
        </FooterButton>
      )}
    </>
  );

  if (sectionTitle) {
    return (
      <Section smallPadding title={sectionTitle} titleLineAddon={printAction} titleVariant={sectionTitleVariant || 'h3'}>
        {tableContent}
      </Section>
    );
  }

  return (
    <>
      {printAction}
      {tableContent}
    </>
  );
};

export default AssetsTable;
